var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { UXPContext } from "@core/context/Context";
import { generateUUID, hasValue } from "@utils";
import { Dropdown } from "@widget/popups/Dropdown";
import { Collapse } from "@widget/other/Collapse";
import { createView, deleteView, getFavouritesByListId, getViewsByListId, toggleFavouriteView, updateView } from "./viewlist-utils";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import classNames from "classnames";
import SearchBox from "@widget/forms/inputs/SearchBox";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { LoadingSpinner } from "@widget/loaders";
import useAlert from "@notifications/Alerts/useAlerts";
import useToast from "@notifications/Toast/useToast";
const ViewsListComponentBase = forwardRef(({ isOpen, onToggle, listId, defaultViews, selectedViewId, onChangeView, addViews, editViews, deleteViews, defaultViewId, hasFiltersChanged = false, hasSortChanged = false, hasPageSizeChanged = false, hasColumnsChanged = false }, ref) => {
    const context = useContext(UXPContext);
    const [views, setViews] = useState({});
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const alerts = useAlert();
    const toast = useToast();
    const FavouriteViewsLabel = context.$L('uxp-core.text.my-favourite');
    const CustomViewsLabel = context.$L('uxp-core.text.custom-views');
    const flattendViews = useMemo(() => Object.values(views).reduce((final, v) => {
        final.push(...v);
        return final;
    }, []), [views]);
    // Check if any changes have been made that would show modification status
    const hasAnyChanges = useMemo(() => hasFiltersChanged || hasSortChanged || hasPageSizeChanged || hasColumnsChanged, [hasFiltersChanged, hasSortChanged, hasPageSizeChanged, hasColumnsChanged]);
    // Get display name for the selected view
    const getDisplayName = useCallback(() => {
        if (!selected)
            return '';
        if (hasAnyChanges)
            return context.$L('uxp-core.text.view-has-modified');
        return selected.name;
    }, [selected, hasAnyChanges, context]);
    const groupAndSort = useCallback((views) => {
        const favourites = [];
        const customViews = [];
        const defaultViews = new Map();
        // Sort views alphabetically by name
        for (const view of views) {
            const groupName = view.group || null;
            if (view.isFavourite) {
                favourites.push(view);
            }
            else if (hasValue(groupName)) {
                if (!defaultViews.has(groupName)) {
                    defaultViews.set(groupName, []);
                }
                defaultViews.get(groupName).push(view);
            }
            else {
                customViews.push(view);
            }
        }
        const result = {};
        if (favourites.length > 0) {
            result[FavouriteViewsLabel] = favourites;
        }
        for (const [groupName, groupViews] of Array.from(defaultViews.entries())) {
            result[groupName] = groupViews;
        }
        if (customViews.length > 0) {
            result[CustomViewsLabel] = customViews;
        }
        return result;
    }, [FavouriteViewsLabel, CustomViewsLabel]);
    const loadViews = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            const customViews = yield getViewsByListId(context, listId);
            const favourites = yield getFavouritesByListId(context, listId);
            const allViews = [...defaultViews, ...customViews].map(v => (Object.assign(Object.assign({}, v), { isFavourite: favourites.findIndex(fv => fv.ViewID == v.id) != -1 })));
            return groupAndSort(allViews);
        }
        catch (e) {
            console.error('Unable to process the views. Error: ', e);
            return {};
        }
        finally {
            setLoading(false);
        }
    }), [context, listId, defaultViews, groupAndSort]);
    const toggleFavourite = useCallback((viewId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (isToggling)
                return;
            setIsToggling(true);
            yield toggleFavouriteView(context, listId, viewId);
        }
        catch (e) {
        }
        finally {
            setIsToggling(false);
            loadViews().then(setViews);
        }
    }), [context, listId, isToggling, loadViews]);
    const saveView = (isNew, name, filters, sort, columns, pageSize, extraConfigs) => __awaiter(void 0, void 0, void 0, function* () {
        const config = { pageSize };
        if (!!filters)
            config.filters = filters || { filters: {} };
        if (!!sort && Object.values(sort).some(s => s !== 0))
            config.sort = Object.entries(sort).reduce((final, [key, val]) => {
                if (val !== 0)
                    final = { [key]: val };
                return final;
            }, {});
        if ((columns === null || columns === void 0 ? void 0 : columns.length) > 0)
            config.columns = columns || [];
        if (extraConfigs)
            Object.assign(config, extraConfigs);
        const id = isNew ? generateUUID() : selected === null || selected === void 0 ? void 0 : selected.id;
        const { error } = yield ((isNew)
            ? createView(context, listId, name, id, config)
            : updateView(context, selected === null || selected === void 0 ? void 0 : selected.key, name, config));
        if (hasValue(error)) {
            alerts.error(error);
            const updatedViews = yield loadViews();
            setViews(updatedViews);
            const viewToSelect = Object.values(updatedViews).flat().find(v => v.id == id);
            if (viewToSelect) {
                onChangeView(Object.assign({}, viewToSelect));
            }
            return false;
        }
        toast.success('Saved');
        const updatedViews = yield loadViews();
        setViews(updatedViews);
        // Select the created/updated view
        const viewToSelect = Object.values(updatedViews).flat().find(v => v.id == id);
        if (viewToSelect) {
            onChangeView(Object.assign({}, viewToSelect));
        }
        return true;
    });
    const getViewDetails = useCallback((id) => {
        if (!hasValue(id) || (flattendViews === null || flattendViews === void 0 ? void 0 : flattendViews.length) == 0)
            return null;
        const _view = flattendViews === null || flattendViews === void 0 ? void 0 : flattendViews.find(v => v.id === id);
        return _view || null;
    }, [flattendViews]);
    useImperativeHandle(ref, () => ({
        save: saveView,
        getViewDetails: getViewDetails,
        getViews: () => flattendViews
    }));
    // Filter views based on search query
    const filteredViews = useMemo(() => {
        if (!query.trim()) {
            return views;
        }
        const searchTerm = query.toLowerCase().trim();
        const filtered = {};
        Object.entries(views).forEach(([groupName, groupViews]) => {
            const matchingViews = groupViews.filter(view => view.name.toLowerCase().includes(searchTerm));
            // Only include groups that have matching views
            if (matchingViews.length > 0) {
                filtered[groupName] = matchingViews;
            }
        });
        return filtered;
    }, [views, query]);
    useEffect(() => {
        loadViews().then(setViews);
    }, [loadViews]);
    useEffect(() => {
        var _a;
        const firstFavourite = flattendViews.find(v => v.isFavourite);
        const vid = hasValue(selectedViewId)
            ? selectedViewId
            : (_a = firstFavourite === null || firstFavourite === void 0 ? void 0 : firstFavourite.id) !== null && _a !== void 0 ? _a : (hasValue(defaultViewId) ? defaultViewId : null);
        const selectedView = hasValue(vid)
            ? flattendViews.find(v => v.id == vid)
            : flattendViews === null || flattendViews === void 0 ? void 0 : flattendViews[0];
        if (!hasValue(selectedViewId)) {
            onChangeView(selectedView);
        }
        else {
            setSelected(selectedView || null);
        }
    }, [views, selectedViewId, defaultViewId, flattendViews]);
    const renderViews = useCallback(() => {
        return _jsxs("div", { className: "uxpcore_obsvl__views", children: [_jsx("div", { className: "uxpcore_obsvl__search", children: _jsx(SearchBox, { value: query, onChange: setQuery, placeholder: context.$L('uxp-core.text.search-views') }) }), Object.keys(filteredViews).length === 0 && query.trim() ? (_jsx("div", { className: "uxpcore_obsvl__no-results", children: context.$L('uxp-core.text.no-views-found') })) : (Object.entries(filteredViews).map(([group, items], i) => {
                    return _jsx(Collapse, { title: group, rightContent: _jsx("div", { className: "uxpcore_obsvl__view-count", children: (items === null || items === void 0 ? void 0 : items.length) || 0 }), defaultExpanded: i < 2 || !!query.trim(), children: items.map((item, j) => {
                            return _jsxs("div", { className: classNames("uxpcore_obsvl__view", {
                                    "uxpcore_obsvl__view--selected": (selected === null || selected === void 0 ? void 0 : selected.id) == item.id,
                                    "uxpcore_obsvl__view--favourite": item === null || item === void 0 ? void 0 : item.isFavourite
                                }), onClick: () => onChangeView(item), children: [_jsx("div", { className: "uxpcore_obsvl__view-label", children: item.name }), _jsx("div", { className: "uxpcore_obsvl__view-action", onClick: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleFavourite(item.id);
                                        }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', isToggling ? 'spinner' : 'star'], spin: isToggling }) })] }, j);
                        }) }, i);
                })), (addViews === null || addViews === void 0 ? void 0 : addViews.enable) && (_jsx("div", { className: "uxpcore_obsvl__newview", onClick: addViews.onTriggerAdd, children: context.$L('uxp-core.text.new-custom-view') }))] });
    }, [filteredViews, selected, query, context, addViews, isToggling, onChangeView, toggleFavourite]);
    function deleteCustomView() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const confirmed = yield alerts.confirm({ title: 'Are you sure? ', content: 'You are about to delete a custom view. This cannot be undone. do you wish to continue?' });
                if (!confirmed)
                    return;
                const { error } = yield deleteView(context, listId, selected === null || selected === void 0 ? void 0 : selected.key);
                if (!!error) {
                    alerts.error(error);
                    const updatedViews = yield loadViews();
                    setViews(updatedViews);
                }
                else {
                    toast.success('Deleted!');
                    const updatedViews = yield loadViews();
                    setViews(updatedViews);
                    // Select the first view after deletion
                    const firstView = (_a = Object.values(updatedViews).flat()) === null || _a === void 0 ? void 0 : _a[0];
                    if (firstView) {
                        onChangeView(firstView);
                    }
                }
            }
            catch (e) {
            }
        });
    }
    function renderActionButtons() {
        if (!(selected === null || selected === void 0 ? void 0 : selected.isCustom) || !(editViews === null || editViews === void 0 ? void 0 : editViews.enable) || !(deleteViews === null || deleteViews === void 0 ? void 0 : deleteViews.enable))
            return;
        return _jsxs("div", { className: "uxpcore_obsvl__selected-actions", children: [(deleteViews === null || deleteViews === void 0 ? void 0 : deleteViews.enable)
                    ? _jsx("div", { className: "uxpcore_obsvl__selected-actions-button", onClick: deleteCustomView, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'trash'] }) })
                    : null, (editViews === null || editViews === void 0 ? void 0 : editViews.enable)
                    ? _jsx("div", { className: "uxpcore_obsvl__selected-actions-button", onClick: editViews === null || editViews === void 0 ? void 0 : editViews.onTriggerEdit, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'pencil'] }) })
                    : null] });
    }
    return _jsxs("div", { className: "uxpcore_obsvl__container", children: [_jsx(Dropdown, { trigger: _jsx("div", { className: classNames("uxpcore_obsvl__selected", {
                        "uxpcore_obsvl__selected--modified": hasAnyChanges
                    }), children: loading
                        ? _jsx(LoadingSpinner, { spinnerOnly: true, styles: { padding: '0.5rem' } })
                        : _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_obsvl__selected-label", children: getDisplayName() }), hasAnyChanges && (_jsx("div", { className: "uxpcore_obsvl__selected-indicator", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'exclamation-circle'] }) })), _jsx(DropdownIndicator, { isOpen: isOpen })] }) }), isOpen: !!isOpen, onToggle: onToggle, content: renderViews(), position: 'bottom-left' }), renderActionButtons()] });
});
export const ViewsList = memo(ViewsListComponentBase);
